<template>
  <div>
    <TieredMenu
      ref="rowMenu"
      id="alert_row_actions_menu"
      :model="rowMenuItems"
      :popup="true"
      class="text-sm"
      :key="rowMenuRowSelected"
    />

    <DataTable
      :value="displayData"
      :paginator="!showSkeletonLoading"
      :rows="props.pagination ? props.pagination?.pageSize || 20 : 20"
      :rowsPerPageOptions="[10, 20, 50, 100]"
      :sortable="!showSkeletonLoading"
      :globalFilterFields="[
        'MERCHANT_NAME',
        'MERCHANT_ID',
        'MERCHANT_OWNER',
        'EVENT_TYPE',
        'ALERT_LEVEL',
      ]"
      class="p-datatable-sm"
      stripedRows
      hover
      responsiveLayout="scroll"
      :selectionMode="props.pagination || !canEdit ? undefined : 'multiple'"
      v-model:selection="selectedAlerts"
      scrollable
      scrollHeight="calc(100vh - 25rem)"
      sortField="EVENT_DATE"
      :sortOrder="-1"
      :totalRecords="
        props.pagination ? props.pagination?.total || 0 : undefined
      "
      :lazy="props.pagination ? true : false"
      @page="onPageChange"
      @rows-change="onRowsChange"
      data-test-id="fcc-alerts-table"
    >
      <Column
        v-if="!props.pagination && canEdit"
        selectionMode="multiple"
        headerStyle="width: 3rem"
      ></Column>

      <Column
        v-if="props.showMerchantColumn"
        field="MERCHANT_NAME"
        header="Supplier"
        sortable
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="8rem" height="1rem" class="mb-2"></Skeleton>
            <Skeleton width="4rem" height="0.75rem"></Skeleton>
          </div>
          <div v-else>
            <button
              type="button"
              class="font-medium text-surface-900 dark:text-surface-0 text-left hover:underline underline-offset-[3px] decoration decoration-primary-500 dark:decoration-primary-400 cursor-pointer"
              :title="data.MERCHANT_NAME"
              @click.stop="viewMerchantDetails(data)"
              :data-test-id="`alert-merchant-btn-${data.ALERT_ID}`"
            >
              {{ data.MERCHANT_NAME }}
            </button>
            <div class="text-xs text-surface-500 dark:text-surface-400">{{ data.MERCHANT_ID }}</div>
          </div>
        </template>
      </Column>
      <Column field="EVENT_TYPE" header="Event" sortable>
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="6rem" height="1rem"></Skeleton>
          </div>
          <button
            v-else
            type="button"
            class="text-sm font-medium text-surface-900 dark:text-surface-0 text-left hover:underline underline-offset-[3px] decoration decoration-primary-500 dark:decoration-primary-400 cursor-pointer"
            :title="formatEventType(data.EVENT_TYPE)"
            @click.stop="viewAlertDetails(data)"
            :data-test-id="`alert-title-btn-${data.ALERT_ID}`"
          >
            {{ formatEventType(data.EVENT_TYPE) }}
          </button>
        </template>
      </Column>
      <Column
        field="TOLERANCE"
        header="Tolerance"
        sortable
        bodyClass="text-center"
        headerClass="text-center"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading" class="flex justify-center">
            <Skeleton
              width="5rem"
              height="1.5rem"
              borderRadius="12px"
            ></Skeleton>
          </div>
          <BadgeFccSeverity
            v-else
            :value="formatTolerance(data.TOLERANCE)"
            :background-color="
              getToleranceColor(data.TOLERANCE).backgroundColor
            "
            :text-color="getToleranceColor(data.TOLERANCE).textColor"
            :border-color="getToleranceColor(data.TOLERANCE).borderColor"
          />
        </template>
      </Column>

      <Column field="STATISTIC" header="Statistic" sortable>
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton
              width="4rem"
              height="1.5rem"
              borderRadius="6px"
            ></Skeleton>
          </div>
          <span
            v-else
            class="text-sm font-semibold px-2 py-1 rounded"
            :class="[
              getSignificanceColor(data.SIGNIFICANCE).textColor,
              getSignificanceColor(data.SIGNIFICANCE).backgroundColor,
            ]"
          >
            {{ formatAlertType(data.STATISTIC, data.EVENT_TYPE) }}
          </span>
        </template>
      </Column>

      <Column field="SIGNIFICANCE" header="Significance" sortable>
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="3rem" height="1rem"></Skeleton>
          </div>
          <span
            v-else
            class="text-sm font-semibold text-surface-900 dark:text-surface-0 w-full text-center"
          >
            {{ formatSignificance(data.SIGNIFICANCE) }}
          </span>
        </template>
      </Column>

      <Column field="EVENT_DATE" header="Event Date" sortable>
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton width="8rem" height="1rem"></Skeleton>
          </div>
          <span v-else class="text-sm text-surface-500 dark:text-surface-400 w-full text-center">{{
            formatDateTimeForDisplay(data.EVENT_DATE)
          }}</span>
        </template>
      </Column>

      <Column
        v-if="canEdit"
        field="CASE_ID"
        header="Case"
        :exportable="false"
        style="width: 6rem"
      >
        <template #body="{ data }">
          <div v-if="showSkeletonLoading">
            <Skeleton
              width="4rem"
              height="1.75rem"
              borderRadius="8px"
            ></Skeleton>
          </div>
          <div v-else class="flex justify-center">
            <Button
              v-if="data.CASE_ID"
              size="small"
              icon="pi pi-external-link"
              label="View"
              variant="text"
              @click.prevent="navigateToCaseDetails(data.CASE_ID)"
              :data-test-id="`alert-case-btn-${data.CASE_ID}`"
            />
          </div>
        </template>
      </Column>

      <Column
        v-if="!props.pagination"
        header="Actions"
        :exportable="false"
        style="width: 7rem"
        class="text-sm font-medium text-surface-600 dark:text-surface-300 border-l border-surface-200 dark:border-surface-700 text-center"
        frozen
        alignFrozen="right"
      >
        <template #body="{ data }">
          <div
            v-if="showSkeletonLoading"
            class="flex justify-center items-center gap-1"
          >
            <Skeleton shape="circle" size="2rem"></Skeleton>
            <Skeleton shape="circle" size="2rem"></Skeleton>
          </div>
          <div v-else class="flex justify-center items-center gap-1">
            <Button
              v-if="canEdit && nonIssueActionAvailable"
              v-tooltip.top="'Mark as Non-Issue'"
              icon="pi pi-check-circle"
              rounded
              variant="outlined"
              size="small"
              aria-label="Mark as Non-Issue"
              :loading="nonIssueInFlight.has(data.ALERT_ID)"
              :disabled="nonIssueInFlight.has(data.ALERT_ID)"
              @click.prevent="confirmNonIssueClick(data)"
              :data-test-id="`non-issue-quick-action-${data.ALERT_ID}`"
            />
            <Button
              icon="pi pi-ellipsis-v"
              rounded
              variant="outlined"
              aria-label="Row Actions"
              @click.prevent="toggleRowMenu($event, data)"
              :data-test-id="`alert-actions-btn-${data.ALERT_ID}`"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <!-- Alert Action Drawer -->
    <AlertActionDrawer
      v-if="canEdit"
      v-model:visible="actionDrawerVisible"
      :selected-alerts="selectedAlertsForAction"
      @action-submitted="handleActionSubmitted"
    />

    <!-- Alert Details Dialog -->
    <AlertDetails
      v-model:visible="alertDetailsVisible"
      :alert="selectedAlert"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, computed } from 'vue'
import { storeToRefs } from 'pinia'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import TieredMenu from 'primevue/tieredmenu'
import Skeleton from 'primevue/skeleton'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import BadgeFccSeverity from '@/components/common/badges/BadgeFccSeverity.vue'
import AlertActionDrawer from './AlertActionDrawer.vue'
import AlertDetails from './AlertDetails.vue'
import { formatDateTimeForDisplay } from '@/util/dateUtils'
import { useFCCAlertStore, useFCCCaseStore, useFCCMerchantStore } from '@/stores/fcc'
import { Alert, AlertHistory } from '@/types/fcc'
import { useFCCFormatting } from '@/util/fcc/useFCCFormatting'
import { useRouter } from 'vue-router'
import { usePerms } from '@/composables/usePerms'
import { PERMISSIONS } from '@/config/permissions'
import { useFrontegg } from '@frontegg/vue'
import { errorToastWithDetails } from '@/util/toasts'
import { normalizeError } from '@/util/errorNormalizer'

interface Props {
  merchantId?: number
  showMerchantColumn?: boolean
  alertsData?: (Alert | AlertHistory)[]
  pagination?: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
  paginationLoading?: boolean
  initialLoading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  merchantId: undefined,
  showMerchantColumn: true,
  alertsData: undefined,
  pagination: undefined,
  paginationLoading: false,
  initialLoading: false,
})

const emit = defineEmits<{
  'page-change': [page: number]
  'page-size-change': [pageSize: number]
  'action-completed': [alertCount: number]
}>()

const alertStore = useFCCAlertStore()
const merchantStore = useFCCMerchantStore()
const router = useRouter()
const frontegg = useFrontegg()
const toast = useToast()
const confirm = useConfirm()
const { hasPerms } = usePerms()
const { filteredAlerts, loading, selectedAlerts } = storeToRefs(alertStore)
const { availableActions } = storeToRefs(merchantStore)

const canEdit = computed(() => hasPerms(PERMISSIONS.FCC_UPDATE))
// Derived from store's availableActions to avoid storeToRefs computed-ref edge cases.
const nonIssueActionAvailable = computed(() =>
  availableActions.value.some(a => a.ACTION_TYPE === 'NON_ISSUE'),
)

// Per-row set of alert IDs currently being submitted as Non-Issue (spinner/disabled state).
const nonIssueInFlight = ref(new Set<number>())

// Determine when to show skeleton loading
const showSkeletonLoading = computed(() => {
  if (props.pagination) {
    // For alert history, show skeleton during initial loading or pagination loading
    return props.initialLoading || props.paginationLoading
  } else {
    // For regular alerts, use the alert store loading state or initial loading
    return loading.value || props.initialLoading
  }
})
const {
  formatEventType,
  formatAlertType,
  formatSignificance,
  getToleranceColor,
  formatTolerance,
  getSignificanceColor,
} = useFCCFormatting()

// Mock data for skeleton loading
const createMockAlert = (index: number): Alert => ({
  ALERT_ID: index,
  EVENT_TYPE: '',
  MERCHANT_ID: 0,
  TIMESTAMP: '',
  STATISTIC: 0,
  THRESHOLD: 0,
  SIGNIFICANCE: 0,
  TOLERANCE: '',
  CASE_ID: 0,
  ACTIONED: false,
  MERCHANT_NAME: '',
  MERCHANT_OWNER: '',
  MCC: '',
  GO_LIVE_DATE: '',
  PLATFORM: '',
  ALERT_LEVEL: undefined,
  DESCRIPTION: '',
})

// Create skeleton rows when pagination is loading
const skeletonRows = computed(() => {
  const length = props.pagination ? props.pagination?.pageSize || 20 : 25
  return Array.from({ length }, (_, index) => createMockAlert(index))
})

// Return skeleton rows when pagination loading, real data otherwise
const displayData = computed(() => {
  if (props.paginationLoading || props.initialLoading) return skeletonRows.value
  return props.alertsData || filteredAlerts.value
})

// Menu and dialog state
const rowMenu = ref(null)
const rowMenuItems = ref<any[]>([])
const rowMenuRowSelected = ref<PropertyKey | undefined>(undefined)
const alertDetailsVisible = ref(false)
const selectedAlert = ref<Alert | null>(null)
const actionDrawerVisible = ref(false)
const selectedAlertsForAction = ref<Alert[]>([])

async function toggleRowMenu(event: Event, rowData: Alert) {
  // Don't show menu for skeleton data or during loading
  if (showSkeletonLoading.value) return

  rowMenuRowSelected.value = rowData.ALERT_ID ?? undefined
  rowMenuItems.value = buildRowMenuItems(rowData)
  await nextTick()
  if (rowMenu.value) {
    ;(rowMenu.value as any).toggle(event)
  }
}

function buildRowMenuItems(rowData: Alert) {
  const items: any[] = []

  items.push({
    label: 'View Alert Details',
    icon: 'pi pi-eye',
    command: () => viewAlertDetails(rowData),
  })

  if (props.showMerchantColumn) {
    // we are likely not on a merchant details page, so we can show this button
    items.push({
      label: 'View Supplier Details',
      icon: 'pi pi-user',
      command: () => viewMerchantDetails(rowData),
    })
  }

  if (canEdit.value) {
    if (rowData.CASE_ID) {
      items.push({
        label: 'View Case',
        icon: 'pi pi-briefcase',
        command: () => navigateToCaseDetails(rowData.CASE_ID),
      })
    }

    items.push({
      label: 'Take Action',
      icon: 'pi pi-bolt',
      command: () => openActionDrawer([rowData]),
    })
  }

  return items
}

function viewAlertDetails(alert: Alert) {
  selectedAlert.value = alert
  alertDetailsVisible.value = true
}

function viewMerchantDetails(alert: Alert) {
  merchantStore.setCurrentMerchantId(alert.MERCHANT_ID)
  merchantStore.setShowMerchantDetails(true)
  router.push({ name: 'fccMerchantDetails', params: { id: alert.MERCHANT_ID } })
}

function openActionDrawer(alerts: Alert[]) {
  selectedAlertsForAction.value = alerts
  actionDrawerVisible.value = true
}

function handleActionSubmitted() {
  const alertCount = selectedAlertsForAction.value.length
  actionDrawerVisible.value = false
  selectedAlertsForAction.value = []
  alertStore.clearSelectedAlerts()
  // Emit event to parent to trigger refresh
  emit('action-completed', alertCount)
}

// Expose method to open action drawer for selected alerts
function openBulkActionDrawer() {
  if (selectedAlerts.value && selectedAlerts.value.length > 0) {
    openActionDrawer(selectedAlerts.value)
  }
}

function confirmNonIssueClick(rowData: Alert) {
  confirm.require({
    header: 'Mark as Non-Issue',
    message: 'Mark this alert as Non-Issue? This closes the alert with no further action.',
    icon: 'pi pi-check-circle',
    rejectProps: { label: 'Cancel', outlined: true },
    acceptProps: {
      label: 'Mark as Non-Issue',
      'data-test-id': 'non-issue-confirm-accept',
    },
    accept: () => handleNonIssueClick(rowData),
    reject: () => {},
  })
}

async function handleNonIssueClick(rowData: Alert) {
  const alertId = rowData.ALERT_ID
  // Double-click guard: skip if already in flight for this row
  if (nonIssueInFlight.value.has(alertId)) return
  nonIssueInFlight.value = new Set([...nonIssueInFlight.value, alertId])
  try {
    await merchantStore.dispositionAsNonIssue(frontegg.authState, [alertId])
    void useFCCCaseStore().refreshCases(frontegg.authState)
    alertStore.clearSelectedAlerts()
    emit('action-completed', 1)
  } catch (err) {
    const apiError = normalizeError(err)
    errorToastWithDetails(
      toast,
      'Failed to mark as Non-Issue',
      apiError.toToastMessage(),
      apiError,
    )
  } finally {
    nonIssueInFlight.value = new Set(
      [...nonIssueInFlight.value].filter(id => id !== alertId),
    )
  }
}

function navigateToCaseDetails(caseId: number) {
  router.push({ name: 'fccCaseDetails', params: { id: caseId.toString() } })
}

// Pagination event handlers
function onPageChange(event: any) {
  if (props.pagination) {
    emit('page-change', event.page + 1) // PrimeVue uses 0-based indexing, API uses 1-based
  }
}

function onRowsChange(event: any) {
  if (props.pagination) {
    emit('page-size-change', event.rows)
  }
}

// Expose for parent component
defineExpose({
  openBulkActionDrawer,
})
</script>
